import BookmarkIcon from '@mui/icons-material/Bookmark';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
  Stack,
  Box,
  Typography,
  CircularProgress,
  SxProps,
  IconButton,
  Divider,
  useTheme,
} from '@mui/material';
import Linkify from 'linkify-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLongPress } from '@/components/hooks/useLongPress';
import ChatUserAvator from '@/components/molecules/ChatUserAvator';
import CommentMenu from '@/components/molecules/CommentMenu';
import ImageFiles from '@/components/molecules/ImageFiles';
import ReactionCounter from '@/components/molecules/ReactionCounter';
import { MyMessagePosition } from '@/components/organisms/chat/ChatFooterMicDual';
import {
  ChatReactionType,
  ChatSessionUser,
  ChatUserRole,
} from '@/types/graphql';
import { getUserName } from '@/utils/userName';
import { isChatEditor, isChatGuest } from '@/utils/userRole';

export type CommentProps = {
  user?: ChatSessionUser;
  userRole: ChatUserRole;
  message: string;
  timestamp: Date;
  isUserVisibled: boolean;
  isMine: boolean;
  isError: boolean;
  isTranslateError: boolean;
  isSending: boolean;
  canGuideControl?: boolean;
  dualLangTranslation?: boolean;
  myMessagePosition: MyMessagePosition;
  isSubUserSend?: boolean;
  transedMessage?: string;
  transedDualMessage?: string;
  reverseMessage?: string;
  originalMessage?: string;
  flag: boolean;
  reactions: { userId: string; reactionType: ChatReactionType }[];
  files: { id: string }[];
  isRemoved: boolean;
  isTyping: boolean;
  reply?: { message: string; userName: string };
  reactionIsOnlyQuestion: boolean;
  onShowMenu?: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onReply: () => void;
  onReaction: (type: ChatReactionType) => void;
  onPlay?: () => void;
  onStop: () => void;
  onChangeFlag?: (value: boolean) => void;
  isPlaying?: boolean;
  replaceNgWord: (msg: string) => string;
  onOpenReactions?: () => void;

  // バケツリレー何とかしたい
  chatRoomId: string;
  sessionEntryCode: string;
  isGuestInputRestricted: boolean;

  isProjectionMode: boolean;
  projectionSubMessages?: string;
  isHiddenInformation: boolean;
};

/**
 * 単一のチャットコメントを表現する
 * @param props
 * @returns
 */
const Comment = (props: CommentProps) => {
  const sentTime = new Date(props.timestamp);
  const borderRadiusForRight = '10px 0 10px 10px';
  const borderRadiusForLeft = '0 10px 10px 10px';

  const { t } = useTranslation('chat');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [isOpenReply, setIsOpenReply] = useState(false);

  const longPressProps = useLongPress(() => setIsOpenMenu(true), 300);

  const sxWithMarginForChatContent: SxProps = useMemo(
    () => ({
      ml: props.isMine ? 1 : '43px',
      mr: props.isMine ? 2 : 1,
    }),
    [props.isMine]
  );
  const chatUser = useMemo(
    () => ({
      chatRoomId: props.chatRoomId,
      sessionEntryCode: props.sessionEntryCode,
    }),
    [props.chatRoomId, props.sessionEntryCode]
  );
  const imageIdsString = props.files.map((x) => x.id).join(','); // 配列の中身に依存させたい苦肉の処理
  const imageIds = useMemo(() => imageIdsString.split(','), [imageIdsString]);

  // NGワードの削除
  const replaceNgWord = props.replaceNgWord;
  const srcMessage = props.message;
  const srcReplyMessage = props.reply?.message;
  const message = useMemo(
    () => replaceNgWord(srcMessage),
    [replaceNgWord, srcMessage]
  );
  const replyMessage = useMemo(
    () =>
      srcReplyMessage != null ? replaceNgWord(srcReplyMessage) : undefined,
    [replaceNgWord, srcReplyMessage]
  );
  const projectionSubMessages = useMemo(
    () => replaceNgWord(props.projectionSubMessages ?? ''),
    [replaceNgWord, props.projectionSubMessages]
  );

  const isGuest = props.user == null || isChatGuest(props.user.userRole);
  const isDualMode = props.dualLangTranslation && props.canGuideControl;
  const alignItems: MyMessagePosition = useMemo(() => {
    const guestMessagePosition =
      props.myMessagePosition === 'start' ? 'end' : 'start';
    if (props.isProjectionMode) {
      if (isGuest) {
        return 'end';
      }

      if (!isGuest) {
        return 'start';
      }
    }

    if (!props.isProjectionMode) {
      if (props.isMine) {
        if (isDualMode) {
          // dual lang mode
          if (props.isSubUserSend) {
            return guestMessagePosition;
          }
          if (!props.isSubUserSend) {
            return props.myMessagePosition;
          }
        }

        if (!isDualMode) {
          return 'end';
        }
      }

      if (!props.isMine) {
        if (isDualMode) {
          return guestMessagePosition;
        }
        return 'start';
      }
    }

    return 'start';
  }, [
    props.isProjectionMode,
    props.isMine,
    isGuest,
    isDualMode,
    props.isSubUserSend,
    props.myMessagePosition,
  ]);

  const theme = useTheme();

  const bgColorChatMessage = useMemo(() => {
    const blue = '#CFEBFA';
    const grey = '#DBE2E5';
    const orange = theme.palette.orange.light;

    if (isDualMode) {
      if (props.isMine) {
        if (props.isSubUserSend) return orange;
        if (!props.isSubUserSend) return blue;
      }

      if (!props.isMine) return grey;
    }

    if (!isDualMode) {
      if (props.isMine) return blue;
      if (!props.isMine) return grey;
    }
  }, [props.isMine, isDualMode, props.isSubUserSend, theme]);

  const getBorderRadiusChatBox = useMemo(() => {
    if (isDualMode) {
      if (props.myMessagePosition === 'start') {
        if (props.isMine) {
          return props.isSubUserSend
            ? borderRadiusForRight
            : borderRadiusForLeft;
        }

        if (!props.isMine) {
          return borderRadiusForRight;
        }
      }
      if (props.myMessagePosition === 'end') {
        if (props.isMine) {
          return props.isSubUserSend
            ? borderRadiusForLeft
            : borderRadiusForRight;
        }

        if (!props.isMine) {
          return borderRadiusForLeft;
        }
      }
    }
    if (!isDualMode) {
      return props.isMine ? borderRadiusForRight : borderRadiusForLeft;
    }
  }, [props.isMine, props.myMessagePosition, props.isSubUserSend, isDualMode]);

  // have avt: 43px  no avt: 2
  const sxWithChatMessageDualMode = useMemo(() => {
    if (props.myMessagePosition === 'start') {
      if (props.isSubUserSend) {
        return {
          mr: props.isMine ? 2 : '43px',
          ml: props.isMine ? 1 : 1,
        };
      }
      if (!props.isSubUserSend) {
        return {
          mr: props.isMine ? 1 : '43px',
          ml: props.isMine ? 2 : 1,
        };
      }
    }

    if (props.myMessagePosition === 'end') {
      if (props.isSubUserSend) {
        return {
          mr: props.isMine ? 1 : 1,
          ml: props.isMine ? 2 : '43px',
        };
      }
      if (!props.isSubUserSend) {
        return {
          mr: props.isMine ? 2 : 1,
          ml: props.isMine ? 1 : '43px',
        };
      }
    }

    return sxWithMarginForChatContent;
  }, [
    props.isMine,
    props.myMessagePosition,
    props.isSubUserSend,
    sxWithMarginForChatContent,
  ]);

  const getDirectionInDualMode = useMemo(() => {
    if (props.isProjectionMode) {
      return isGuest ? 'row-reverse' : 'row';
    }

    if (!isDualMode) {
      return props.isMine ? 'row-reverse' : 'row';
    }

    if (props.myMessagePosition === 'start') {
      if (props.isSubUserSend) {
        return 'row-reverse';
      }
      if (!props.isSubUserSend) {
        if (props.isMine) {
          return 'row';
        }

        if (!props.isMine) {
          return 'row-reverse';
        }
      }
    }

    if (props.myMessagePosition === 'end') {
      if (props.isSubUserSend) {
        return 'row';
      }
      if (!props.isSubUserSend) {
        if (props.isMine) {
          return 'row-reverse';
        }

        if (!props.isMine) {
          return 'row';
        }
      }
    }

    return 'row';
  }, [
    props.isMine,
    props.myMessagePosition,
    props.isSubUserSend,
    props.isProjectionMode,
    isGuest,
    isDualMode,
  ]);

  const isDual =
    props.canGuideControl &&
    props.dualLangTranslation &&
    !props.isProjectionMode &&
    props.isMine;
  return (
    <Stack
      alignItems={alignItems}
      py="6px"
      sx={
        isGuest || props.isProjectionMode
          ? undefined
          : {
              paddingTop: '10px',
              borderBottom: 1,
              borderTop: 1,
              borderColor: (t) => t.palette.grey[300],
              background: (t) => t.palette.background.paper,
            }
      }
    >
      {/* ユーザーアイコンとユーザー名 */}
      {props.isUserVisibled && !props.isProjectionMode && (
        <>
          {isDualMode && props.myMessagePosition === 'start' ? (
            <Stack direction="row" alignItems="center" mr="10px">
              <Typography
                variant="body2"
                color={(t) => t.palette.grey[700]}
                mx={1}
                fontWeight={'bold'}
              >
                {getUserName(
                  props.user?.userName,
                  !props.isMine &&
                    props.isSubUserSend &&
                    props.dualLangTranslation
                )}
              </Typography>
              <ChatUserAvator userRole={props.userRole} />
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" ml="10px">
              <ChatUserAvator userRole={props.userRole} />
              <Typography
                variant="body2"
                color={(t) => t.palette.grey[700]}
                mx={1}
                fontWeight={'bold'}
              >
                {getUserName(
                  props.user?.userName,
                  !props.isMine &&
                    props.isSubUserSend &&
                    props.dualLangTranslation
                )}
              </Typography>
            </Stack>
          )}
        </>
      )}
      {/* 時間とメッセージ表示 */}
      <Stack
        direction={getDirectionInDualMode}
        alignItems="flex-end"
        sx={isDualMode ? sxWithChatMessageDualMode : sxWithMarginForChatContent}
        spacing={1}
      >
        {props.isRemoved === false ? (
          <Box
            // 吹き出しBOX
            ref={setMenuAnchor}
            border={1}
            borderColor={(t) =>
              props.isPlaying ? t.palette.primary.main : '#0000'
            }
            bgcolor={bgColorChatMessage}
            sx={{
              textAlign: 'left',
              borderRadius: getBorderRadiusChatBox,
              cursor: props.onPlay == null ? 'default' : 'pointer',
              position: 'relative',

              // 音声再生時のブルーアニメーション
              animation: props.isPlaying
                ? 'width-increase 1s ease-in-out infinite'
                : undefined,
              '@keyframes width-increase': {
                '0%': {
                  boxShadow: (t) =>
                    `0px 0px 0px rgb(${t.palette.primary.main},0)`,
                },
                '50%': {
                  boxShadow: (t) => `0px 0px 16px ${t.palette.primary.main}`,
                },
                '100%': {
                  boxShadow: (t) =>
                    `0px 0px 0px rgb(${t.palette.primary.main},0)`,
                },
              },
              background: props.isProjectionMode ? 'none' : 'default',
            }}
            onContextMenu={
              isChatEditor(props.isGuestInputRestricted, props.userRole)
                ? (e) => {
                    e.preventDefault();
                    props.onShowMenu?.();
                    setIsOpenMenu(true);
                  }
                : undefined
            }
            onClick={() => props.onPlay?.()}
            {...(isChatEditor(props.isGuestInputRestricted, props.userRole)
              ? longPressProps
              : {})}
            data-testid={
              props.isMine ? 'chat-comment-mine' : 'chat-comment-other'
            }
          >
            {props.reply != null && (
              <>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpenReply((x) => !x);
                  }}
                >
                  <Stack>
                    {!props.isProjectionMode && (
                      <Typography
                        variant="caption"
                        width="100%"
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        fontWeight="bold"
                        px={2}
                        mt={1}
                      >
                        {props.reply.userName}
                      </Typography>
                    )}
                    <Box
                      position="relative"
                      width="100%"
                      height={isOpenReply ? undefined : '23px'}
                      pb={1}
                    >
                      <Typography
                        width="100%"
                        px={2}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        position={isOpenReply ? undefined : 'absolute'}
                        whiteSpace={isOpenReply ? undefined : 'nowrap'}
                        variant={isOpenReply ? undefined : 'caption'}
                      >
                        {replyMessage}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Divider />
              </>
            )}
            {isDual && (
              <Box px={props.isProjectionMode ? 0 : 2} py={1}>
                <Typography sx={{}} mt={0.5} variant="caption">
                  {message}
                </Typography>
                {props.isTranslateError ? (
                  <Typography variant="caption" color="error">
                    {t('translateError')}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      whiteSpace: 'pre-line',
                      overflowWrap: 'anywhere',
                    }}
                    fontWeight={isGuest ? 'normal' : 'bold'}
                  >
                    {isGuest ? (
                      props.transedDualMessage
                    ) : (
                      <Linkify>{props.transedDualMessage}</Linkify>
                    )}
                    {props.isTyping && (
                      <CircularProgress
                        size={14}
                        sx={{ ml: '4px', mb: '-2px' }}
                      />
                    )}
                  </Typography>
                )}
                {props.isRemoved === false && props.reverseMessage && (
                  <Typography sx={{}} mt={0.5} variant="caption">
                    {props.reverseMessage}
                  </Typography>
                )}
                {props.files.length > 0 && (
                  <ImageFiles imageIds={imageIds} chatUser={chatUser} />
                )}
              </Box>
            )}
            {!isDual && (
              <Box px={props.isProjectionMode ? 0 : 2} py={1}>
                <Typography
                  sx={{
                    whiteSpace: 'pre-line',
                    overflowWrap: 'anywhere',
                  }}
                  fontWeight={isGuest ? 'normal' : 'bold'}
                >
                  {isGuest ? message : <Linkify>{message}</Linkify>}
                  {props.isTyping && (
                    <CircularProgress
                      size={14}
                      sx={{ ml: '4px', mb: '-2px' }}
                    />
                  )}
                </Typography>
                {props.files.length > 0 && (
                  <ImageFiles imageIds={imageIds} chatUser={chatUser} />
                )}
              </Box>
            )}
            {props.flag && (
              <BookmarkIcon
                color="primary"
                fontSize="small"
                sx={{
                  position: 'absolute',
                  right: props.isMine ? undefined : '-1px',
                  left: props.isMine ? '-1px' : undefined,
                  top: '-5px',
                }}
              />
            )}
          </Box>
        ) : (
          <Typography variant="caption">
            {t('comment.removedMessage')}
          </Typography>
        )}
        {props.isError && (
          <ErrorOutlineOutlinedIcon color="error" sx={{ mb: '6px' }} />
        )}
        {props.isSending && (
          <Typography variant="caption">{t('comment.sending')}</Typography>
        )}
        {props.isError === false && props.isSending === false && (
          <Stack
            direction={'column'}
            alignItems={'center'}
            justifyContent={'flex-end'}
          >
            {props.isPlaying && (
              <IconButton
                sx={{ p: 0, mt: '-100px', mb: '-4px' }}
                onClick={props.onStop}
              >
                <StopCircleIcon />
              </IconButton>
            )}
            {!props.isHiddenInformation && (
              <Typography variant="caption" color={(t) => t.palette.grey[600]}>
                {`${sentTime.getHours().toString().padStart(2, '0')}:${sentTime
                  .getMinutes()
                  .toString()
                  .padStart(2, '0')}`}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
      {/* 翻訳 */}
      {props.isRemoved === false &&
        props.originalMessage &&
        !props.isHiddenInformation && (
          <Typography
            sx={sxWithMarginForChatContent}
            mt={0.5}
            variant="caption"
          >
            {props.originalMessage}
          </Typography>
        )}
      {props.isRemoved === false && !isDualMode && props.transedMessage && (
        <Typography sx={sxWithMarginForChatContent} mt={0.5} variant="caption">
          {props.transedMessage}
        </Typography>
      )}
      {props.isRemoved === false && !isDualMode && props.reverseMessage && (
        <Typography sx={sxWithMarginForChatContent} mt={0.5} variant="caption">
          {props.reverseMessage}
        </Typography>
      )}
      {props.isRemoved === false &&
        props.projectionSubMessages &&
        props.isProjectionMode && (
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              overflowWrap: 'anywhere',
              mt: 0.5,
              ml: props.isMine ? 1 : '43px',
              mr: props.isMine ? 2 : 1,
            }}
            fontWeight={isGuest ? 'normal' : 'bold'}
            variant="body2"
          >
            {projectionSubMessages}
          </Typography>
        )}
      {/* リアクション */}
      <Stack
        sx={sxWithMarginForChatContent}
        mt={0.5}
        alignItems={
          props.isProjectionMode
            ? isGuest
              ? 'flex-end'
              : 'flex-start'
            : props.isMine
            ? 'flex-end'
            : 'flex-start'
        }
      >
        <ReactionCounter
          reactions={props.reactions}
          onOpenReactions={props.onOpenReactions}
        />
      </Stack>
      <CommentMenu
        onReaction={props.isError ? undefined : props.onReaction}
        onDelete={
          props.isError || props.isMine === false ? undefined : props.onDelete
        }
        onEdit={props.isMine === false ? undefined : props.onEdit}
        onReply={props.isError ? undefined : props.onReply}
        onPlay={props.onPlay}
        onChangeFlag={props.onChangeFlag}
        flag={props.flag}
        isOwnMenu={props.isMine}
        reactionIsOnlyQuestion={props.reactionIsOnlyQuestion}
        open={Boolean(isOpenMenu)}
        onClose={() => setIsOpenMenu(false)}
        anchorEl={menuAnchor}
      />
    </Stack>
  );
};
export default Comment;
